import Breadcrumbs from "@@/components/Breadcrumb";
import { SnwButtonType } from "@@/components/Button/buttonProps";
import SnwButton from "@@/components/Button/SnwButton";
import UnorderedList, { ListItemProp } from "@@/components/UnorderedList";
import { Token } from "@@/design-tokens/colors";
import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import Picture from "@@/elements/Picture";
import Typography from "@@/elements/Typography";
import { ScreenSize, useIsBreakpointOrBelow } from "@@/hooks/device/use-breakpoint";
import { PaddingValue } from "@@/hooks/ui/use-spacing";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import { LinkData } from "@@/models/linkdata";
import { PictureData } from "@@/models/pictureData";
import styles from "./index.module.scss";
import { useEffect, useRef, useState } from "react";
import useScrollPosition from "@@/hooks/use-scroll-position";

type OfferHeroEditableProps = {
  header: string;
  intro?: string;
  image?: PictureData;
  listItems?: ListItemProp[];
  button?: SnwButtonType;
};

type OfferHeroProps = {
  label: string;
  backgroundColor?: "beige" | "blue" | "green";
  breadcrumbItems: LinkData[];
  breadcrumbsBackgroundColor?: "white" | "beige" | "blue" | "green" | "orange" | "grey";
  showSticky: boolean;
};

type HeroContentPageEditableProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  hero: OfferHeroEditableProps;
  // For now we will add epi edit for whole component since epi couldn't handle the on
  // page edit for components thats not placed inside a content block.
};

type OfferPageProperties = OfferHeroProps & OfferHeroEditableProps;

export const OfferHero = ({
  header,
  label,
  intro,
  backgroundColor,
  listItems,
  button,
  image,
  breadcrumbItems,
  breadcrumbsBackgroundColor = "white",
  showSticky = true,
} : OfferPageProperties) => {
  const [sticky, setSticky] = useState<boolean>(false);
  const ref = useRef(null);
  const scrollPosition = useScrollPosition();
  
  useEffect(() => {
    if (scrollPosition && ref.current) {
      const { offsetTop, offsetHeight } = ref.current;
      setSticky(scrollPosition > (offsetTop + offsetHeight));
    }
  }, [ref, scrollPosition]);

  const getBackgroundColor = (color: "white" | "beige" | "blue" | "green" | "orange" | "grey", light: boolean = false) : Token => {
    const colorTintNumber = light ? 4 : 3;
    const beigeTintNumber = light ? 3 : 2;
    switch (color) {
    case "blue":
      return `PrimaryBlueTint0${colorTintNumber}`;
    case "green":
      return `PrimaryGreenTint0${colorTintNumber}`;
    case "white":
      return "White";
    case "orange":
      return "ComplementaryOrangeTint04";
    case "grey":
      return "PrimaryGreyTint03";
    case "beige":
    default:
      return `ComplementaryBeigeTint0${beigeTintNumber}`;
    }
  };

  const defaultPadding : PaddingValue = [14, 18, 18];
  const imagePadding : PaddingValue = [10, 14, 18];
  const isSmall = useIsBreakpointOrBelow(ScreenSize.SMALL);
  return (
    <Container
      size="full"
      pageGutter={0}>
      <Box
        backgroundColor={getBackgroundColor(backgroundColor)}
        pt={[...defaultPadding]}
        pb={[...(image ? imagePadding : defaultPadding)]}
      >
        <Container
          component="div"
          pageGutterY={0}>
            
          <Box
            ref={ref}
            className={styles.offer_header_wrapper}
            gap={10}>
            <EpiProperty<HeroContentPageEditableProps> name="hero">
              <Box>
                <Typography
                  variant="heading5"
                  asElement="div">
                  {label}
                </Typography>
                <hr className={styles.offer_header_divider} />
                <Typography
                  mb={0}
                  asElement="h1"
                  variant="heading1">
                  {header}
                </Typography>
              </Box>
            </EpiProperty>
            <Box
              p={[6, 8, 8]}
              flexDirection="column"
              gap={button ? 6 : 0}
              br={1}
              backgroundColor={getBackgroundColor(backgroundColor, true)}
            >
              <Box>
                <EpiProperty<OfferHeroEditableProps> name="intro">
                  {intro && (
                    <Typography
                      pb={listItems && listItems.length > 0 ? 4 : 0}
                      variant={isSmall ? "body" : "intro"}>{intro}</Typography>
                  )}
                </EpiProperty>
                <EpiProperty<OfferHeroEditableProps> name="listItems">
                  <Box>
                    <UnorderedList
                      listItems={listItems}
                      textVariant={isSmall ? "body" : "intro"} />
                  </Box>
                </EpiProperty>
              </Box>
              {button && (
                <SnwButton {...button} />
              )}
              {showSticky && (button && button.link.text && button.link.href) && (
                <Box
                  className={sticky ? styles.sticky_button : styles.hidden}
                  backgroundColor="White"
                  py={4}
                  px={6}>
                  <SnwButton
                    link={button.link}
                    variant="primary" />
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
      <EpiProperty<HeroContentPageEditableProps> name="hero">
        <Box>
          {image && (
            <>
              <Picture
                className={styles.offer_header_image}
                imageData={image}
                aspectRatio={"6 / 1"}
                sizes={[
                  [300, 600, 900, 1200],
                  [300, 600, 900, 1200, 1800],
                  [900, 1200, 1800, 2400, 3000, 3840],
                ]}
                lazySizes={false}
                loading="eager"
                sourcesSizes="(min-widht: 1920px) 1920px, 100vw"
              />
            </>
          )}
          <Box
            pt={[4, 4, 8]}
            backgroundColor={getBackgroundColor(breadcrumbsBackgroundColor, breadcrumbsBackgroundColor === "beige")}>
            <Container pageGutterY={0}>
              <Breadcrumbs breadcrumbItems={breadcrumbItems} />
            </Container>
          </Box>
        </Box>
      </EpiProperty>
    </Container>
  );
};

export type ProvidedOfferHeroProps =
  OptimizelyBlockProps<OfferPageProperties> & OfferPageProperties;

const ProvidedOfferHero = ({
  blockId,
  inEditMode,
  properties,
  ...rest
}: ProvidedOfferHeroProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
  >
    {(optimizlyProperties) => (
      <OfferHero
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedOfferHero;
